/**
 * `GET /me/brand-color` as `queryOptions` — ONE cache entry for every reader: the signed-in shell
 * that paints the app, `/konto`'s Kolor marki tab that edits it, and the session-start prefetch
 * that warms it beside `/auth/me`. The key stands outside `/konto`'s `['account']` prefix, so an
 * account-wide invalidation does not refetch the colour.
 *
 * An absent row is not an error: the endpoint answers `{ preset: 'Flame' }` for a trainer who has
 * never chosen, so there is no empty state to distinguish.
 *
 * The wire shape is narrowed in `select`, not at the call site: a projection built in a hook body
 * is a new object on every render, and the React Compiler cannot cache what the colour is derived
 * into downstream. `select` runs once per response instead, and every consumer reads one identity.
 * `toBrandColor` is passed BY NAME rather than wrapped in an arrow, because TanStack memoises a
 * select's result on the function's identity as well as the data's.
 *
 * `refetchOnWindowFocus: 'always'`: the other place this colour changes is the trainer's phone.
 * The app-wide default refetches on focus only once the 30s `staleTime` has passed, so a colour
 * picked on mobile and checked on the desk a few seconds later would not show. The read is a few
 * bytes, and a focus is a deliberate return to the tab.
 */

import { queryOptions } from '@tanstack/react-query';

import { getMeBrandColor } from '~/shared/api/generated/endpoints/my-brand-color/my-brand-color';

import { toBrandColor } from '../model/brandColor';

export const brandColorQueryKey = ['brand-color'] as const;

export function brandColorQueryOptions() {
  return queryOptions({
    queryKey: brandColorQueryKey,
    queryFn: ({ signal }) => getMeBrandColor(undefined, signal),
    select: toBrandColor,
    refetchOnWindowFocus: 'always',
  });
}
